import { AlertCircle, CheckCircle, Info, Loader2, Search, XCircle } from 'lucide-react';
import { cn } from '../utils/helpers';

export function PageHeader({ eyebrow, title, description, icon: Icon, action, className }) {
  return (
    <div className={cn('dashboard-surface p-5 sm:p-6', className)}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          {Icon && (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-primary-400/25 bg-primary-500/10 shadow-lg shadow-primary-600/10">
              <Icon className="h-6 w-6 text-primary-300" />
            </div>
          )}
          <div>
            {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-300">{eyebrow}</p>}
            <h1 className="mt-1 text-2xl font-black text-white sm:text-3xl">{title}</h1>
            {description && <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-400">{description}</p>}
          </div>
        </div>
        {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
      </div>
    </div>
  );
}

export function SectionCard({ title, description, icon: Icon, action, children, className, bodyClassName }) {
  return (
    <section className={cn('card', className)}>
      {(title || description || action) && (
        <div className="border-b border-white/10 p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              {Icon && (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                  <Icon className="h-5 w-5 text-primary-300" />
                </div>
              )}
              <div>
                {title && <h2 className="text-lg font-bold text-white">{title}</h2>}
                {description && <p className="mt-1 text-sm text-gray-400">{description}</p>}
              </div>
            </div>
            {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
          </div>
        </div>
      )}
      <div className={cn('p-5 sm:p-6', bodyClassName)}>{children}</div>
    </section>
  );
}

export function EmptyState({ icon: Icon = Search, title = 'Nada encontrado', description, action, className }) {
  return (
    <div className={cn('rounded-3xl border border-dashed border-white/10 bg-white/[0.025] px-6 py-12 text-center', className)}>
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
        <Icon className="h-7 w-7 text-primary-300" />
      </div>
      <h3 className="mt-4 text-lg font-bold text-white">{title}</h3>
      {description && <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-400">{description}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

export function InlineAlert({ type = 'info', children, className }) {
  const styles = {
    success: 'border-green-500/20 bg-green-500/10 text-green-200',
    error: 'border-red-500/20 bg-red-500/10 text-red-200',
    warning: 'border-yellow-500/20 bg-yellow-500/10 text-yellow-100',
    info: 'border-blue-500/20 bg-blue-500/10 text-blue-100'
  };
  const icons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertCircle,
    info: Info
  };
  const Icon = icons[type] || Info;
  return (
    <div className={cn('flex items-start gap-3 rounded-2xl border p-4 text-sm', styles[type], className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

export function LoadingState({ label = 'Carregando...' }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-gray-400">
      <Loader2 className="h-5 w-5 animate-spin text-primary-400" />
      <span className="text-sm">{label}</span>
    </div>
  );
}
